import { z } from 'zod';

/**
 * FilterRule — конструктор правил фильтра (BE-8, концепт `filter-columns-concept.md`).
 *
 * Один `adhocFilter = { match, rules[] }` редактируется тремя видами (фильтр-карточка
 * FE-35, активные чипы FE-37, per-column FE-40) — плоский AND/OR, не дерево групп
 * (PM не аналитик). `field` — системный ключ ИЛИ `cf:<key>` (custom-field §11.21).
 *
 * Operator зависит от типа поля; пара field×operator валидируется на BE по
 * `FieldDefinition.type` (невалидная → 422 structural). См. `filterRuleOperators`.
 */

/** Полный набор операторов (объединение всех типов полей). */
export const FilterOperator = z.enum([
  // select / status / priority / label / entity_ref / subject_type
  'in_list',
  'not_in',
  // assignee / author / checkbox / single-value
  'eq',
  'neq',
  // any nullable
  'empty',
  'not_empty',
  // text / url
  'contains',
  'starts_with',
  // number / decimal / money
  'gte',
  'lte',
  'between',
  // date / datetime — пресеты (просрочено/сегодня/неделя) + диапазон
  'preset',
]);
export type FilterOperator = z.infer<typeof FilterOperator>;

/** Пресеты для date-операторов (`operator='preset'`). */
export const FilterDatePreset = z.enum(['overdue', 'today', 'this_week', 'no_date']);
export type FilterDatePreset = z.infer<typeof FilterDatePreset>;

/**
 * Семантические категории поля → допустимые операторы. Системные поля имеют
 * статически известную категорию; `cf:<key>` резолвится через `FieldType` на BE.
 */
export const filterFieldKind = z.enum([
  'select', // status / priority / label / subject_type / single_select / multi_select / entity_ref
  'user', // assignee / author / user_picker
  'date', // due / start / date / datetime
  'number', // number / decimal / money
  'text', // title / description / text / url / rich_text
  'bool', // checkbox
]);
export type FilterFieldKind = z.infer<typeof filterFieldKind>;

/** Допустимые операторы по категории поля (валидация field×operator). */
export const filterRuleOperators: Record<FilterFieldKind, FilterOperator[]> = {
  select: ['in_list', 'not_in', 'empty', 'not_empty'],
  user: ['in_list', 'not_in', 'eq', 'neq', 'empty', 'not_empty'],
  date: ['gte', 'lte', 'between', 'preset', 'empty', 'not_empty'],
  number: ['eq', 'neq', 'gte', 'lte', 'between', 'empty', 'not_empty'],
  text: ['contains', 'starts_with', 'eq', 'neq', 'empty', 'not_empty'],
  bool: ['eq'],
};

/**
 * Системные filterable-поля → категория. Ключи совпадают с колонками задачи
 * (или junction-фасетами). `cf:<key>` обрабатывается отдельно по FieldType.
 */
export const systemFilterFields: Record<string, FilterFieldKind> = {
  status: 'select', // state.group (нормализованная группа состояния)
  priority: 'select',
  project: 'select', // task.project_id (кросс-проектный дэшборд)
  entity: 'select', // tasks-by-company B6 — task.entity_id (фильтр по компании в holding-агрегате)
  label: 'select', // junction taskModuleTaskLabels
  subject_type: 'select',
  assignee: 'user', // junction taskModuleTaskAssignees
  author: 'user', // BE-2 — постановщик (task.author_user_id, скаляр)
  due_date: 'date',
  start_date: 'date',
  title: 'text',
  description: 'text',
};

/** `field` правила: системный ключ ∪ `cf:<snake_case_key>`. */
export const FilterFieldRef = z
  .string()
  .min(1)
  .max(44)
  .regex(/^(cf:[a-z][a-z0-9_]*|[a-z][a-z0-9_]*)$/, 'field — системный ключ или cf:<key>');
export type FilterFieldRef = z.infer<typeof FilterFieldRef>;

export const FilterRule = z
  .object({
    field: FilterFieldRef,
    operator: FilterOperator,
    /** Type-specific значение; форма проверяется на BE по operator+field-kind. */
    value: z.unknown(),
  })
  // zod-strict-ok: dual-use, input-validated в SavedViewCreate + /tasks/search (contract-тест требует strict)
  .strict();
export type FilterRule = z.infer<typeof FilterRule>;

export const FilterMatch = z.enum(['all', 'any']);
export type FilterMatch = z.infer<typeof FilterMatch>;

/**
 * AdhocFilter — снимок rule-based фильтра. Персистится в
 * `task_module_saved_views.filter` рядом с legacy-фасетами (back-compat).
 */
export const AdhocFilter = z
  .object({
    match: FilterMatch,
    rules: z.array(FilterRule).max(50),
  })
  // zod-strict-ok: input snapshot, not FE-parsed
  .strict();
export type AdhocFilter = z.infer<typeof AdhocFilter>;

/** `cf:<key>` → `<key>`; системное поле → null (не custom). */
export function customFieldKey(field: string): string | null {
  return field.startsWith('cf:') ? field.slice(3) : null;
}

/**
 * `FieldType` (custom-поле) → категория фильтра. `import type { FieldType }`
 * избегается циклом — карта по строковому литералу типа.
 */
export const fieldTypeToKind: Record<string, FilterFieldKind> = {
  text: 'text',
  rich_text: 'text',
  url: 'text',
  number: 'number',
  decimal: 'number',
  money: 'number',
  date: 'date',
  datetime: 'date',
  single_select: 'select',
  multi_select: 'select',
  entity_ref: 'select',
  user_picker: 'user',
  checkbox: 'bool',
};
